import type { RecordValue, Scope, Unit } from "./types";

export type CompetitionStructureInput = {
  scope: Scope;
  title: string;
  stageTitle: string;
  startsAtEpochMillis?: number; endsAtEpochMillis?: number;
  stageStartsAtEpochMillis?: number; stageEndsAtEpochMillis?: number;
  rounds: readonly { title: string; weightBps?: number; startsAtEpochMillis?: number; endsAtEpochMillis?: number }[];
};
export type CompetitionStructureStep = Readonly<{
  unitId: string;
  requestId: string;
  code: string;
  title: string;
  unitKind: "COMPETITION" | "STAGE" | "VOTING_ROUND";
  parentUnitId?: string;
  startsAtEpochMillis?: number;
  endsAtEpochMillis?: number;
  roundWeightBps?: number;
}>;
export type CompetitionStructurePlan = Readonly<{
  scope: Readonly<Scope>;
  rootUnitId: string;
  stageUnitId: string;
  steps: readonly CompetitionStructureStep[];
}>;
export type CompetitionStructureProgress = {
  completedCount: number;
  totalCount: number;
  currentStep: CompetitionStructureStep | null;
};
export type CompetitionStructureIO = {
  readUnit: (unitId: string, signal?: AbortSignal) => Promise<Unit>;
  createUnit: (body: RecordValue, signal?: AbortSignal) => Promise<{ unit: Unit }>;
  /** Only confirmed API rejections qualify; transport errors and unknown outcomes do not. */
  isDefinitiveFailure?: (error: unknown) => boolean;
};

export class CompetitionStructureError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "CompetitionStructureError";
    this.code = code;
  }
}

function validTitle(value: string): string {
  const title = value.trim();
  if (!title || title.length > 200) throw new CompetitionStructureError("INVALID_TITLE", "Every competition, stage and round needs a name of 1–200 characters.");
  return title;
}

/** Preparing a review allocates identities only; it never reads or creates server records. */
export function createCompetitionStructurePlan(input: CompetitionStructureInput, makeId: () => string = () => crypto.randomUUID()): CompetitionStructurePlan {
  const title = validTitle(input.title);
  const stageTitle = validTitle(input.stageTitle);
  if (!/^[A-Za-z0-9][A-Za-z0-9_.-]{0,95}$/.test(input.scope.organizerId) || !/^[A-Za-z0-9][A-Za-z0-9_.-]{0,95}$/.test(input.scope.serviceProductId))
    throw new CompetitionStructureError("INVALID_SCOPE", "A selected organization and service are required.");
  if (input.rounds.length < 1 || input.rounds.length > 32 || input.rounds.some(round => (round.weightBps === undefined || !Number.isSafeInteger(round.weightBps) || round.weightBps <= 0 || round.weightBps > 10000 || round.weightBps % 100 !== 0)) || input.rounds.reduce((total, round) => total + (round.weightBps || 0), 0) > 10000)
    throw new CompetitionStructureError("INVALID_WEIGHTS", "Choose 1-32 rounds. Assigned whole percentages cannot exceed 100%.");
  const dates = (start?: number, end?: number) => {
    if ([start, end].some(value => value !== undefined && (!Number.isSafeInteger(value) || value < 0 || value > 253402300799000)) || (start !== undefined && end !== undefined && end <= start)) throw new CompetitionStructureError("INVALID_DATES", "End must be after start.");
    return { ...(start !== undefined ? { startsAtEpochMillis: start } : {}), ...(end !== undefined ? { endsAtEpochMillis: end } : {}) };
  };
  const rounds = input.rounds.map(round => ({ title: validTitle(round.title), ...(round.weightBps !== undefined ? { roundWeightBps: round.weightBps } : {}), ...dates(round.startsAtEpochMillis, round.endsAtEpochMillis) }));
  const identities = new Set<string>();
  const nextId = () => {
    const id = makeId();
    if (!/^[A-Za-z0-9][A-Za-z0-9_.-]{0,79}$/.test(id) || identities.has(id))
      throw new CompetitionStructureError("INVALID_IDENTITY", "Each planned request and unit must have a distinct valid identity.");
    identities.add(id);
    return id;
  };
  const rootUnitId = nextId();
  const stageUnitId = nextId();
  const steps: CompetitionStructureStep[] = [
    { unitId: rootUnitId, requestId: nextId(), code: `competition-${rootUnitId}`, title, unitKind: "COMPETITION", ...dates(input.startsAtEpochMillis, input.endsAtEpochMillis) },
    { unitId: stageUnitId, requestId: nextId(), code: `stage-${stageUnitId}`, title: stageTitle, unitKind: "STAGE", parentUnitId: rootUnitId, ...dates(input.stageStartsAtEpochMillis, input.stageEndsAtEpochMillis) },
    ...rounds.map(round => {
      const unitId = nextId();
      return { unitId, requestId: nextId(), code: `round-${unitId}`, ...round, unitKind: "VOTING_ROUND" as const, parentUnitId: stageUnitId };
    }),
  ];
  return Object.freeze({ scope: Object.freeze({ ...input.scope }), rootUnitId, stageUnitId, steps: Object.freeze(steps.map(step => Object.freeze(step))) });
}

/** Sequential creation retains acknowledged records and the exact request with an unknown outcome. */
export class CompetitionStructureRunner {
  readonly plan: CompetitionStructurePlan;
  private saved = 0;
  private active = false;
  private pendingBody: Readonly<RecordValue> | null = null;
  private failure: "unknown" | "rejected" | null = null;
  private replacementRequestId: string | null = null;
  private readonly makeId: () => string;

  constructor(plan: CompetitionStructurePlan, makeId: () => string = () => crypto.randomUUID()) {
    this.plan = plan;
    this.makeId = makeId;
  }
  get completedCount() { return this.saved; }
  get complete() { return this.saved === this.plan.steps.length; }
  get pending() { return this.pendingBody; }
  get failureKind() { return this.failure; }

  /** Called after the user reviews a confirmed rejection, never to rewrite an uncertain request. */
  reopenRejectedStep(): boolean {
    if (this.active || this.failure !== "rejected" || !this.pendingBody) return false;
    const requestId = this.makeId();
    if (!/^[A-Za-z0-9][A-Za-z0-9_.-]{0,95}$/.test(requestId) || requestId === this.pendingBody.requestId || this.plan.steps.some(step => step.requestId === requestId || step.unitId === requestId))
      throw new CompetitionStructureError("INVALID_IDENTITY", "The reviewed retry needs a fresh request identity.");
    this.replacementRequestId = requestId;
    this.pendingBody = null;
    this.failure = null;
    return true;
  }

  async run(io: CompetitionStructureIO, options: { signal?: AbortSignal; onProgress?: (progress: CompetitionStructureProgress) => void } = {}): Promise<{ rootUnitId: string; stageUnitId: string }> {
    if (this.active) throw new CompetitionStructureError("ALREADY_RUNNING", "This structure is already being created.");
    this.active = true;
    const notify = () => options.onProgress?.({ completedCount: this.saved, totalCount: this.plan.steps.length, currentStep: this.plan.steps[this.saved] || null });
    try {
      notify();
      while (!this.complete) {
        options.signal?.throwIfAborted();
        const step = this.plan.steps[this.saved];
        if (!this.pendingBody) {
          let parent: Unit | undefined;
          if (step.parentUnitId) {
            parent = await io.readUnit(step.parentUnitId, options.signal);
            options.signal?.throwIfAborted();
            this.assertScope(parent, step.parentUnitId);
            if (parent.unitKind !== (step.unitKind === "STAGE" ? "COMPETITION" : "STAGE") || parent.status !== "DRAFT" || !["EMPTY", "UNITS"].includes(String(parent.contentMode)))
              throw new CompetitionStructureError("PARENT_CHANGED", "The selected parent can no longer receive draft stages or rounds.");
          }
          this.pendingBody = Object.freeze({ ...step,
            requestId: this.replacementRequestId || step.requestId,
            ...(parent ? { expectedParentVersion: parent.version } : this.plan.scope),
          });
          this.replacementRequestId = null;
        }
        this.failure = null;
        let response: { unit: Unit };
        try {
          response = await io.createUnit(this.pendingBody as RecordValue, options.signal);
        } catch (error) {
          this.failure = io.isDefinitiveFailure?.(error) ? "rejected" : "unknown";
          throw error;
        }
        try {
          this.assertScope(response?.unit, step.unitId);
          if (response.unit.unitKind !== step.unitKind || (response.unit.parentUnitId || undefined) !== step.parentUnitId)
            throw new CompetitionStructureError("INVALID_RESPONSE", "The saved unit does not match the reviewed structure.");
        } catch (error) {
          // A malformed acknowledgement cannot prove the mutation did not commit.
          this.failure = "unknown";
          throw error;
        }
        this.saved += 1;
        this.pendingBody = null;
        notify();
      }
      return { rootUnitId: this.plan.rootUnitId, stageUnitId: this.plan.stageUnitId };
    } finally {
      this.active = false;
    }
  }

  private assertScope(unit: Unit | undefined, expectedUnitId: string) {
    if (!unit || unit.unitId !== expectedUnitId || unit.organizerId !== this.plan.scope.organizerId || unit.serviceProductId !== this.plan.scope.serviceProductId || !Number.isSafeInteger(unit.version) || unit.version < 1)
      throw new CompetitionStructureError("SCOPE_CHANGED", "The unit no longer matches the reviewed organization and service.");
  }
}
